<script lang="ts">
  import { _ } from "svelte-i18n";
  import { push } from "svelte-spa-router";
  import { acceptInvitation, loadSession } from "$lib/auth";
  import ConsentText from "$components/ConsentText.svelte";

  let { params }: { params: { token: string } } = $props();

  let fullName = $state("");
  let password = $state("");
  let acceptTerms = $state(false);
  let confirmPassword = $state("");
  let showPassword = $state(false);
  let showConfirm = $state(false);
  /* Only flag a mismatch once the second field has been typed into. */
  const passwordsMismatch = $derived(
    confirmPassword.length > 0 && password !== confirmPassword,
  );
  let loading = $state(false);
  let error = $state<string | null>(null);
  let success = $state(false);

  async function handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    if (password !== confirmPassword) {
      error = $_("auth.error.passwordsDoNotMatch");
      return;
    }
    if (password.length < 12) {
      error = $_("auth.error.passwordTooShort");
      return;
    }

    if (!acceptTerms) {
      error = $_("consent.required");
      return;
    }

    loading = true;
    error = null;

    try {
      const res = await acceptInvitation({
        token: params.token,
        full_name: fullName,
        password: password,
        accept_terms: acceptTerms,
      });
      success = true;
      if (res?.auto_logged_in && res?.slug) {
        // Session is already established server-side — go straight in.
        await loadSession();
        setTimeout(() => push(`/t/${res.slug}/dashboard`), 1200);
      } else {
        // Existing account: the invite attached a membership but credentials
        // are unchanged — sign in normally.
        setTimeout(() => push("/login?reason=invite-accepted"), 2000);
      }
    } catch (err: any) {
      error = err.message || $_("auth.error.invalidToken");
    } finally {
      loading = false;
    }
  }
</script>

<div class="auth-page">
  <div class="auth-card">
    <div class="brand">
      <img src="/logo.svg" alt="Order Evolution" class="logo" />
      <h2>{$_("auth.invite.title")}</h2>
      <p class="subtitle">{$_("auth.invite.subtitle")}</p>
    </div>

    {#if success}
      <div class="success-message">
        <span class="material-icons">check_circle</span>
        <p>{$_("auth.invite.success")}</p>
        <p class="hint">{$_("auth.invite.redirecting")}</p>
      </div>
    {:else}
      <form onsubmit={handleSubmit} class="auth-form">
        {#if error}
          <div class="error-alert">{error}</div>
        {/if}

        <div class="field">
          <label for="fullName">{$_("auth.fullName")}</label>
          <input
            type="text"
            id="fullName"
            bind:value={fullName}
            required
            placeholder="John Doe"
            disabled={loading}
          />
        </div>

        <div class="field">
          <label for="password">{$_("auth.newPassword")}</label>
          <div class="pw-wrap">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              autocomplete="new-password"
              bind:value={password}
              required
              minlength="12"
              placeholder="••••••••"
              disabled={loading}
            />
            <button
              type="button"
              class="pw-toggle"
              aria-label={showPassword ? $_("auth.password.hide") : $_("auth.password.show")}
              aria-pressed={showPassword}
              title={showPassword ? $_("auth.password.hide") : $_("auth.password.show")}
              onclick={() => (showPassword = !showPassword)}
            >
              <span class="material-icons-outlined" aria-hidden="true">
                {showPassword ? "visibility_off" : "visibility"}
              </span>
            </button>
          </div>
        </div>

        <div class="field">
          <label for="confirm">{$_("auth.confirmPassword")}</label>
          <div class="pw-wrap">
            <input
              type={showConfirm ? "text" : "password"}
              id="confirm"
              autocomplete="new-password"
              bind:value={confirmPassword}
              required
              minlength="12"
              placeholder="••••••••"
              disabled={loading}
              class:input--invalid={passwordsMismatch}
              aria-invalid={passwordsMismatch}
            />
            <button
              type="button"
              class="pw-toggle"
              aria-label={showConfirm ? $_("auth.password.hide") : $_("auth.password.show")}
              aria-pressed={showConfirm}
              title={showConfirm ? $_("auth.password.hide") : $_("auth.password.show")}
              onclick={() => (showConfirm = !showConfirm)}
            >
              <span class="material-icons-outlined" aria-hidden="true">
                {showConfirm ? "visibility_off" : "visibility"}
              </span>
            </button>
          </div>
          {#if passwordsMismatch}
            <p class="field-err">{$_("auth.error.passwordsDoNotMatch")}</p>
          {/if}
        </div>

        <label class="consent">
          <input type="checkbox" bind:checked={acceptTerms} required disabled={loading} />
          <ConsentText />
        </label>

        <button type="submit" class="btn btn--primary btn--lg" disabled={loading || !acceptTerms}>
          {#if loading}
            <span class="spinner"></span>
            {$_("common.processing")}...
          {:else}
            {$_("auth.invite.submitBtn")}
          {/if}
        </button>
      </form>
    {/if}
  </div>
</div>

<style>
  .auth-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
    padding: 20px;
  }
  .auth-card {
    width: 100%;
    max-width: 440px;
    background: white;
    padding: 40px;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.05);
  }
  .brand { text-align: center; margin-bottom: 32px; }
  .logo { height: 48px; margin-bottom: 16px; }
  .brand h2 { margin: 0; font-size: 24px; color: var(--color-surface-dark); }
  .subtitle { margin: 8px 0 0; color: var(--color-text-muted); }

  .auth-form { display: flex; flex-direction: column; gap: 20px; }
  .field { display: flex; flex-direction: column; gap: 8px; }
  .field label { font-size: 13px; font-weight: 600; color: var(--color-surface-dark); }
  .field input {
    padding: 12px;
    border: 1px solid var(--color-divider);
    border-radius: 6px;
    font-size: 15px;
    transition: border-color 0.2s;
  }
  .field input:focus { border-color: var(--color-primary); outline: none; }

  /* Password field with the reveal toggle sitting inside the input. */
  .pw-wrap { position: relative; display: flex; }
  .pw-wrap input { width: 100%; box-sizing: border-box; padding-right: 44px; }
  .pw-toggle {
    position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; padding: 0;
    border: none; background: none; cursor: pointer; border-radius: 50%;
    color: var(--color-text-muted);
  }
  .pw-toggle:hover { color: var(--color-primary); background: rgba(33,133,213,0.08); }
  .pw-toggle .material-icons-outlined { font-size: 20px; }
  .input--invalid { border-color: var(--color-danger); }
  .field-err { margin: 0; font-size: 12px; color: var(--color-danger); }
  .consent { display: flex; gap: 10px; align-items: flex-start; font-size: 13px; line-height: 1.45; color: var(--color-text); cursor: pointer; }
  .consent input { margin-top: 2px; flex-shrink: 0; }

  .btn--lg { padding: 14px; font-size: 16px; font-weight: 600; border-radius: 6px; cursor: pointer; }
  .btn--primary { background: var(--color-primary); color: white; border: none; }
  .btn--primary:hover { opacity: 0.9; }
  .btn:disabled { opacity: 0.7; cursor: not-allowed; }

  .error-alert {
    padding: 12px;
    background: #fff5f5;
    border: 1px solid #ffc1c1;
    color: #e53935;
    border-radius: 6px;
    font-size: 14px;
    text-align: center;
  }

  .success-message {
    text-align: center;
    padding: 20px 0;
  }
  .success-message .material-icons { font-size: 64px; color: #4caf50; margin-bottom: 16px; }
  .success-message p { margin: 8px 0; font-size: 18px; font-weight: 600; }
  .success-message .hint { font-size: 14px; font-weight: 400; color: var(--color-text-muted); }

  .spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    margin-right: 8px;
    vertical-align: middle;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
